import React, { useEffect, useState, useMemo, type ReactElement } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, type TableColumnType, Switch, Space, Flex, Typography } from 'antd'
import { AddCircle16Regular, SubtractCircle16Regular, Info12Regular } from '@fluentui/react-icons'
import { MSLayoutContentRow, MSLayoutTitleRow, showMSError } from '@choreograph/layout'

import { UserStatus, type ICreateEditPanelState, type IUserPermission } from '../../../constants/types/common'
import { transformRelationship, removeDuplicates, NOTIFICATION_DURATION, pageTitlePadding, getNamespaceInfoLink } from '../../../constants/helpers/common'
import { type IOktaUser } from '../../../constants/types/generatedTypesAliases'
import { useGetRelationshipsQuery } from '../../../state/apis/enhancedRelationshipsApi'
import { useGetUsersQuery } from '../../../state/apis/enhancedUsersApi'
import { selectNamespace, selectSelectedUser, setSelectedUser } from '../../../state/slices/appSlice'
import { InfoTag, ButtonWithTooltip } from '../../shared'
import {
  UserTableActions,
  RemoveUserPermissionsPanel,
  CreateOrEditUserPermissionsPanel,
  UserDetailsPanel,
  UserTable
} from '../'
import { ADD_PERMISSIONS, REMOVE_PERMISSIONS } from '../../../components/shared/constants'

export const UserListContainer = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userId: userIdParam } = useParams()
  const [searchFilter, setSearchFilter] = useState({})
  const { data: users = [] as IOktaUser[], isLoading: usersLoading } = useGetUsersQuery(searchFilter)
  const selectedUser = useSelector(selectSelectedUser)
  const namespace = useSelector(selectNamespace)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedTableUsers, setSelectedTableUsers] = useState<IOktaUser[]>([])
  const [createEditPanelState, setCreateEditPanelState] = useState<ICreateEditPanelState>(null)
  const [removePermissionDrawerVisible, setRemovePermissionDrawerVisible] = useState<boolean>(false)
  const [activeTabKey, setActiveTabKey] = useState<string>('1')
  const [includeInactive, setIncludeInactive] = useState<boolean>(false)

  const statusFilteredUsers = useMemo(() => {
    if (includeInactive) {
      return users
    }
    return users.filter(u => u.status === UserStatus.ACTIVE)
  }, [users, includeInactive])

  const subjectIds = selectedUser ? [selectedUser.id] : selectedRowKeys
  const { data: relationships = [], isLoading: permissionsLoading, error } = useGetRelationshipsQuery({
    serviceAuthorisationDomainModelsFiltersAuthRelationshipFilter: {
      subjectIds
    }
  }, { skip: !(selectedUser?.id || removePermissionDrawerVisible) })

  const namespaceDocumentationLink = useMemo(() => getNamespaceInfoLink(namespace), [namespace])

  const permissions: IUserPermission[] = useMemo(() => {
    const permissionResult = relationships.map(relationship => (
      transformRelationship(relationship)
    ))

    return removeDuplicates(permissionResult)
  }, [relationships])

  useEffect(() => {
    if (error) {
      if ('data' in error) {
        const errorData = error.data as { filter: string[] } | string[]
        if (Array.isArray(errorData)) {
          const messages = errorData
          messages.forEach(message => {
            showMSError({ duration: NOTIFICATION_DURATION, message })
          })
        } else if ('filter' in errorData) {
          const messages = errorData.filter
          messages.forEach(message => {
            showMSError({ duration: NOTIFICATION_DURATION, message })
          })
        } else {
          showMSError({ duration: NOTIFICATION_DURATION, message: 'Error while receiving permissions' })
        }
      }
    }
  }, [error])

  useEffect(() => {
    if (userIdParam && !usersLoading) {
      const user = users.find(u => u.id === userIdParam)
      dispatch(setSelectedUser(user as IOktaUser ?? null))
    }
  }, [usersLoading, users, dispatch, userIdParam])

  useEffect(() => {
    if (!usersLoading) {
      const result: IOktaUser[] = selectedRowKeys
        .flatMap(key => {
          const found = users.find(el => el.id === key)
          return found ? [found] : []
        })
      setSelectedTableUsers(result)
    }
  }, [selectedRowKeys, users, usersLoading])

  const closeDetailsDrawer = () => {
    dispatch(setSelectedUser(null))
    navigate('/users', { replace: true })
    setActiveTabKey('1')
  }

  const openDetailsDrawer = (user: IOktaUser) => {
    const { id: userId } = user
    dispatch(setSelectedUser(user))
    setActiveTabKey('2')
    navigate(`${userId}`, { replace: true })
    setSelectedRowKeys([])
  }

  const openAddPermission = (user: IOktaUser) => {
    setSelectedTableUsers([user])
    setCreateEditPanelState({ operation: 'create' })
  }

  const userTableActions: TableColumnType<IOktaUser> = {
    title: '',
    width: 130,
    render: (value) => {
      return (
        <UserTableActions
          user={value}
          addPermissions={openAddPermission}
          openUserDetails={openDetailsDrawer}
        />
      )
    }
  }

  const handleRowClick = (record) => ({
    onClick: (e) => {
      /* ignore icon buttons in the UserTableActions */
      if (e.target instanceof SVGElement ||
        e.target instanceof HTMLButtonElement ||
        !namespace) {
        return
      }
      const userId = record.id
      navigate(`${userId}`, { replace: true })
      setSelectedRowKeys([])
    }
  })

  const bulkActionButtons: ReactElement = (
    <span>
      <InfoTag
        text={`${selectedRowKeys.length} selected`}
        onClose={() => setSelectedRowKeys([])}
      />
      <ButtonWithTooltip
        namespaceDependency={true}
        tooltipText={REMOVE_PERMISSIONS}
        color='danger'
        variant='text'
        onClick={() => setRemovePermissionDrawerVisible(true)}
        icon={<SubtractCircle16Regular />}>
        {REMOVE_PERMISSIONS}
      </ButtonWithTooltip>
      <ButtonWithTooltip
        namespaceDependency={true}
        tooltipText={ADD_PERMISSIONS}
        color='primary'
        variant='text'
        icon={<AddCircle16Regular />}
        onClick={() => setCreateEditPanelState({ operation: 'create' })}>
        Add a Permission
      </ButtonWithTooltip>
    </span>
  )

  return (
    <>
      {namespace && (
        <MSLayoutContentRow>
          <Typography.Link href={namespaceDocumentationLink} target='_blank' className='namespace-info-link'>
            <Info12Regular />
            <span>
              Click here to read the {namespace} namespace documentation
            </span>
          </Typography.Link>
        </MSLayoutContentRow>
      )}
      <MSLayoutTitleRow innerPadding={pageTitlePadding}>
        Users
      </MSLayoutTitleRow>
      <MSLayoutContentRow>
        <Card>
          <Flex justify='flex-end'>
            <Space align='center' className='users__include-inactive-switch'>
              <span>Include inactive:</span>
              <Switch
                title='include-inactive-switch'
                onChange={(checked: boolean) => setIncludeInactive(checked)} />
            </Space>
          </Flex>
          <UserTable
            tableData={statusFilteredUsers}
            loading={usersLoading}
            selectedRowKeys={selectedRowKeys as string[]}
            setSelectedRowKeys={setSelectedRowKeys}
            rowActionButtons={userTableActions}
            bulkActionButtons={bulkActionButtons}
            onRow={handleRowClick}
            searchFilter={searchFilter}
            setSearchFilter={setSearchFilter}
          />
        </Card>
      </MSLayoutContentRow>
      <UserDetailsPanel
        permissions={permissions}
        permissionsLoading={permissionsLoading}
        selectedUser={selectedUser}
        closeDrawer={closeDetailsDrawer}
        activeKey={activeTabKey}
        setActiveKey={setActiveTabKey}
      />

      <CreateOrEditUserPermissionsPanel
        closable={false}
        selectedUsers={selectedTableUsers}
        createEditPanelState={createEditPanelState}
        setCreateEditPanelState={setCreateEditPanelState}
      />

      <RemoveUserPermissionsPanel
        permissions={permissions}
        permissionsLoading={permissionsLoading}
        selectedUsers={selectedTableUsers}
        removePermissionDrawerVisible={removePermissionDrawerVisible}
        setRemovePermissionDrawerVisible={setRemovePermissionDrawerVisible}
      />
    </>
  )
}
